import { comparerVersions } from './versionCompare';

/**
 * Verdict de version : ce que l'application doit faire de la version installée sur l'appareil.
 *
 * Logique PURE, séparée de la lecture réseau et de React : c'est la seule pièce du mécanisme qui
 * décide de retirer l'application à son utilisateur, et elle doit pouvoir être exercée sans
 * simulacre de requête ni rendu.
 */

export interface VersionPolicy {
  /** Dernière version publiée sur le store. Au-dessous : bannière ignorable. */
  latestVersion: string;
  /** Version minimale utilisable. Au-dessous : écran bloquant. */
  minimumVersion: string;
  /** Destination du bouton de mise à jour, lue en base (voir `0042_app_versions.sql`). */
  storeUrl: string;
}

/**
 * `inconnu` n'est pas un état d'attente : c'est un refus de conclure, et il se comporte exactement
 * comme `ok` — rien ne s'affiche. Les deux restent distincts parce qu'ils n'affirment pas la même
 * chose : `ok` dit « tu es à jour », `inconnu` dit « je n'ai pas pu le déterminer ».
 */
export type Verdict = 'inconnu' | 'ok' | 'maj-disponible' | 'bloque';

/**
 * FAIL-OPEN PAR CONSTRUCTION. Le blocage exige que DEUX choses soient lisibles : la version
 * installée et la version minimale. Tout le reste — politique absente, réseau muet, colonne
 * renommée, valeur mal saisie — produit `inconnu`, et `inconnu` n'a jamais bloqué personne.
 *
 * Ce choix a un prix, assumé : le coupe-circuit tombe en même temps que ce qu'il protège. Une panne
 * Supabase désarme le blocage. L'inverse transformerait une panne d'une heure en désinstallations.
 */
export function evaluerVersion(installee: unknown, politique: VersionPolicy | null): Verdict {
  if (!politique) return 'inconnu';

  // Le minimum d'abord : c'est le champ critique, et il se suffit. Une `latest_version` illisible ne
  // doit pas empêcher un blocage légitime — la contrainte SQL garantit déjà minimum ≤ latest.
  const versusMinimum = comparerVersions(installee, politique.minimumVersion);
  if (versusMinimum === null) return 'inconnu';
  if (versusMinimum < 0) return 'bloque';

  const versusDerniere = comparerVersions(installee, politique.latestVersion);
  if (versusDerniere === null) return 'inconnu';
  return versusDerniere < 0 ? 'maj-disponible' : 'ok';
}
